import { inject, Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { RoleService } from '../../core-module/services/role.service';
import { JwtTokenService } from './jwt-token.service';
import { AuthService } from './auth.service';
import { AccountService } from '../../core-module/services/account.service';
import { ErrorReportService } from '../../core-module/services/error-report.service';
import { IErrorInfo } from '../models/error-info';
import { AuthStore } from '@scl/core/domains/auth/services/auth.store';

@Injectable({
  providedIn: 'root'
})
export class RoutingService {
  private readonly authStore = inject(AuthStore);

  constructor(
    private accountService: AccountService,
    private router: Router,
    private jwtTokenService: JwtTokenService,
    private authService: AuthService,
    private errorReportService: ErrorReportService
  ) { }

  navigate(commands: any[], extras?: NavigationExtras) {
    this.router.navigate(commands, extras);
  }

  navigateAuthUser(force?: boolean) {
    // In Clerk mode, redirect to the Clerk login page instead of the IdentityServer OIDC flow.
    // Also skip when provider config hasn't loaded yet to avoid a premature OIDC redirect.
    if (!this.jwtTokenService.accessToken && !this.authStore.isClerk() && this.authStore.isProviderReady()) {
      this.authService.initCodeAuthorizationPkce();
    }

    if (!force) {
      this.navigateByRoles();
      return;
    }

    // This will first navigate to a temporary route with skipLocationChange: true (so it doesn't appear in the browser history), 
    // then navigate to the target branch report, 
    // which forces a complete reload of the component.

    // Navigate to the account route first, then go back
    // skipLocationChange set to true means:
    // - The URL will not change in the browser
    // - The navigation will not be recorded in the browser history
    this.router.navigateByUrl('/account', { skipLocationChange: true }).then(() =>
      this.navigateByRoles());
  }

  private navigateByRoles() {
    if (!this.accountService.currentUser) {
      return;
    }

    const isInternalUser = this.accountService.isInternalUser();
    if (isInternalUser) {
      this.navigate(['dashboard']);
    } else {
      this.navigate(['home']);
    }
  }

  navigateToError(errorInfo: IErrorInfo): void {
    this.errorReportService.setError(errorInfo);
    this.router.navigate(['/error']);
  }
}
